import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import SearchInput from '../../../../components/shared/search-input';
import { Dropdown, Option } from '../../../../components/shared/nextgen/dropdown';
import MobileFilter from '../../../../components/shared/mobile-dialog';
import { Lx } from '../../../../i18n/consts';

import styles from '../../projects.module.scss';

export type MobileView = 'list' | 'filter';

export const DEFAULT_PROJECT_FILTER_STATE = {
    search: '',
    selectedStatus: -1,
};

export interface ProjectTypeFilterProps {
    search: string;
    selectedStatus: number;
    isMobile: boolean;
    mobileView: MobileView;
    onFilterChange: (payload: { search: string; selectedStatus: number }) => void;
    onMobileClose: () => void;
    className?: string;
    children: React.ReactNode;
}

export const ProjectTypeFilter: React.FC<ProjectTypeFilterProps> = ({
    search,
    selectedStatus,
    isMobile,
    mobileView,
    onFilterChange,
    onMobileClose,
    className,
    children,
}) => {
    const intl = useIntl();

    const statusOptions = useMemo(() => [
        { id: -1, name: intl.formatMessage({ id: Lx.General.ALL_STATUSES }) },
        { id: 0, name: intl.formatMessage({ id: Lx.General.ACTIVE }) },
        { id: 1, name: intl.formatMessage({ id: Lx.General.SUSPENDED }) },
    ], [intl]);

    const [pendingSearch, setPendingSearch] = useState<string>(search);
    const [pendingStatus, setPendingStatus] = useState<number>(selectedStatus);

    useEffect(() => {
        if (isMobile && mobileView === 'filter') {
            setPendingSearch(search);
            setPendingStatus(selectedStatus);
        }
    }, [isMobile, mobileView, search, selectedStatus]);

    useEffect(() => {
        if (!isMobile) {
            setPendingSearch(search);
            setPendingStatus(selectedStatus);
        }
    }, [isMobile, search, selectedStatus]);

    const handleMobileApply = () => {
        onFilterChange({ search: pendingSearch, selectedStatus: pendingStatus });
        onMobileClose();
    };

    const handleMobileReset = () => {
        setPendingSearch(DEFAULT_PROJECT_FILTER_STATE.search);
        setPendingStatus(DEFAULT_PROJECT_FILTER_STATE.selectedStatus);
        onFilterChange(DEFAULT_PROJECT_FILTER_STATE);
    };

    const handleDesktopSearchChange = (value: string) => {
        onFilterChange({ search: value, selectedStatus });
    };

    const handleDesktopStatusChange = (value: number) => {
        onFilterChange({ search, selectedStatus: value });
    };

    const renderFilterContent = (
        searchValue: string,
        statusValue: number,
        mobile: boolean,
        onSearchValueChange: (value: string) => void,
        onStatusValueChange: (value: number) => void,
    ) => {
        const containerClass = mobile ? styles.mobileFilter : (className ?? styles.filterRow);

        return (
            <div className={containerClass}>
                <div className={styles.filterControls}>
                    <div className={styles.searchField}>
                        <SearchInput
                            searchValue={searchValue}
                            onChange={onSearchValueChange}
                            width={mobile ? '100%' : '260px'}
                        />
                    </div>
                    <div className={styles.statusDropdown}>
                        <Dropdown
                            style={{
                                width: mobile ? '100%' : '260px',
                                marginBottom: 0,
                                fontSize: '16px',
                            }}
                            selectedTextColor="secondary"
                            name='statusFilter'
                            value={statusValue}
                            placeholder={intl.formatMessage({ id: Lx.General.ALL_STATUSES })}
                            onChange={(_, value) => onStatusValueChange(value as number)}>
                            {statusOptions.map(option => (
                                <Option key={option.id} value={option.id}>
                                    {option.name}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
            </div>
        );
    };

    if (isMobile && mobileView === 'filter') {
        return (
            <MobileFilter
                onCancel={handleMobileReset}
                onConfirm={handleMobileApply}
                cancelLocalizationId={Lx.General.RESET_FILTER}
            >
                {renderFilterContent(
                    pendingSearch,
                    pendingStatus,
                    true,
                    setPendingSearch,
                    setPendingStatus,
                )}
            </MobileFilter>
        );
    }

    return (
        <div className={styles.pageContent}>
            {!isMobile ? renderFilterContent(
                search,
                selectedStatus,
                false,
                handleDesktopSearchChange,
                handleDesktopStatusChange,
            ) : null}
            {children}
        </div>
    );
};
